import { Avatar, Box, Typography, Stack, Tooltip } from '@mui/material';

import { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { saveUserRecentActivity } from 'common/api';
import highIcon from 'common/assets/images/high-circle.svg';
import lowIcon from 'common/assets/images/low-circle.svg';
import mediumIcon from 'common/assets/images/medium-circle.svg';
import TableComponent from 'common/components/Table/Table';
import { appPages } from 'common/constants/TextContent';
import { setQueryFlag } from 'common/store/reducer/commonReducer';
import { setTaxonomyDetailsTabIndex } from 'common/store/reducer/mappingDetailsReducer';
import { truncateString, urlWithTenant } from 'common/utils/utilFunctions';

import { IRegulationListTable } from '../../RegulationChange/api/type-def';

import { Column } from '../../../../common/components/Table/CommonTableStyles';
import { ENTITY_TYPE } from '../../../../common/constants/TaxonomyConstants';
import { RootState } from '../../../../common/store/AppStore';

import styles from './ListTable.module.scss';

export const formatAssigneeName = (name: string) => {
	const words = name.split(' ');
	const result = words.map((words) => words.charAt(0).toUpperCase()).join('');
	return result;
};

interface ISearchProps {
	data: IRegulationListTable[];
	isLoading: boolean;
	totalRecordsCount: number;
	handlePageChange: (data: any, flag?: boolean) => void;
	handleSortChange: (data: any) => void;
	defaultQuery?: any;
}

const ListTable = (props: ISearchProps) => {
	const {
		data,
		isLoading,
		totalRecordsCount,
		handlePageChange,
		handleSortChange,
		defaultQuery,
	} = props;
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [listTableData, setListTableData] = useState<IRegulationListTable[]>(
		[],
	);

	const loggedInUser = useSelector(
		(state: RootState) => state?.common?.loggedInUser,
	);

	const { REG_CHANGE_PAGE } = appPages;

	const navigationToCitationDetail = async (
		id: string,
		name: string,
		updateId: string,
	) => {
		dispatch(setQueryFlag(true));
		dispatch(setTaxonomyDetailsTabIndex('overview'));
		const now = new Date();
		const formattedTime = now.toISOString();
		const payload = {
			entityName: name,
			entityId: id,
			userId: loggedInUser?.userId as string,
			entityOperation: 'READ',
			operationTimestamp: formattedTime,
			entityType: 'citation',
		};

		const bodyData = { ...payload };

		const responseFromApi = await saveUserRecentActivity(bodyData);
		if (responseFromApi.statusCode === 201) {
			navigate(urlWithTenant(`/regulation-change/${id}`), {
				state: {
					citationId: id,
					entityId: id,
					isCitationView: true,
					entityName: name,
					routePath: REG_CHANGE_PAGE,
					entityType: ENTITY_TYPE.CITATION,
					entityUpdateId: updateId || '',
					displayLabel: REG_CHANGE_PAGE,
				},
			});
		}
	};

	const navigationToRegulationDetails = async (
		regulationId: string,
		officeName: string,
	) => {
		dispatch(setTaxonomyDetailsTabIndex('overview'));
		const formattedTime = new Date().toISOString();
		const payload = {
			entityName: officeName,
			entityId: regulationId,
			userId: loggedInUser?.userId as string,
			entityOperation: 'READ',
			operationTimestamp: formattedTime,
			entityType: 'regulation',
		};

		const bodyData = { ...payload };

		const response = await saveUserRecentActivity(bodyData);
		if (response.statusCode === 201) {
			navigate(urlWithTenant(`/regulation-change/${regulationId}`), {
				state: {
					entityType: ENTITY_TYPE.REGULATION,
					entityId: regulationId,
					isCitationView: false,
					entityName: officeName,
					routePath: REG_CHANGE_PAGE,
					displayLabel: REG_CHANGE_PAGE,
				},
			});
		}
	};

	const columns: readonly Column[] = [
		{
			id: 'citationTitle',
			label: 'Citation name',
			disablePadding: false,
			isSortable: true,
			render: (params: IRegulationListTable) => (
				<Typography
					onClick={() =>
						navigationToCitationDetail(
							params.citationId,
							params.citationTitle,
							params.citationUpdateId,
						)
					}
					className={styles['navigate-data-field']}
					data-testid="citation-link"
				>
					{params?.citationTitle && params?.citationTitle.length > 35 ? (
						<Tooltip title={params?.citationTitle}>
							<span>{`${truncateString(params?.citationTitle, 35)}...`}</span>
						</Tooltip>
					) : (
						<span>{`${truncateString(params?.citationTitle, 35)}`}</span>
					)}
				</Typography>
			),
		},
		{
			id: 'regOfficialId',
			label: 'Regulation',
			disablePadding: false,
			isSortable: true,
			render: (params: IRegulationListTable) => (
				<Typography
					onClick={() =>
						navigationToRegulationDetails(
							params.regulationId,
							params.regOfficialId,
						)
					}
					className={styles['navigate-data-field']}
					data-testid="regulation-link"
				>
					{params.regOfficialId}
				</Typography>
			),
		},
		{ id: 'reviewDeadLine', label: 'Review deadline', disablePadding: false },
		{
			id: 'priority',
			label: 'Priority',
			disablePadding: false,
			isSortable: true,
			render: (params: IRegulationListTable) => (
				<Stack direction="row">
					<div>
						{(params.priority === 'High' && (
							<img
								src={highIcon}
								alt="High"
								className={styles['priority-icon']}
							/>
						)) ||
							(params.priority === 'Medium' && (
								<img
									src={mediumIcon}
									alt="Medium"
									className={styles['priority-icon']}
								/>
							)) ||
							(params.priority === 'Low' && (
								<img
									src={lowIcon}
									alt="Low"
									className={styles['priority-icon']}
								/>
							))}
					</div>
					<Typography className={styles['text-styling']}>
						{params.priority}
					</Typography>
				</Stack>
			),
		},
		{
			id: 'agency',
			label: 'Regulator',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'taskOpenCount',
			label: 'Open tasks',
			disablePadding: false,
			render: (params: IRegulationListTable) => (
				<Typography className={styles['text-styling']}>
					{params.taskOpenCount}/{params.taskTotalCount}
				</Typography>
			),
		},
		{
			id: 'assignedUser',
			label: 'Assignee',
			isSortable: true,
			disablePadding: false,
			render: (params: IRegulationListTable) => {
				let formatedName = '';
				if (params.assignedUser) {
					formatedName = formatAssigneeName(params.assignedUser);
				}
				return (
					<Typography className={styles['table-row-avatar']}>
						{formatedName && (
							<Avatar className={styles['risks-table-cell-avatar']}>
								{formatedName}
							</Avatar>
						)}
					</Typography>
				);
			},
		},
		{ id: 'status', label: 'Status', disablePadding: false, isSortable: true },
	];
	useEffect(() => {
		if (data) {
			setListTableData(data);
		}
	}, [data]);

	return (
		<Box
			className={styles['risks-table-box']}
			data-testid="regulation-list-table"
		>
			<TableComponent
				defaultQuery={defaultQuery}
				rows={listTableData}
				columns={columns}
				totalItems={totalRecordsCount}
				defaultSortKey=""
				onPageHandler={handlePageChange}
				isLoading={isLoading}
				tableStyles={{ minWidth: 750, maxHeight: 440 }}
				onSortHandler={handleSortChange}
				enableHeaderColor
				isRenderPagination
			/>
		</Box>
	);
};

export default ListTable;
